import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "364",
  "slug": "what-causes-the-seasons",
  "title": "What Causes the Seasons? The Geography of Earth's Tilt",
  "excerpt": "It's a common myth that summer happens when Earth is closest to the Sun. The real reason is the planet's tilt, and it explains everything from long summer evenings to the polar night.",
  "date": "2026-06-07",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1445855743215-296f71d4b49c?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxhdXR1bW4lMjBsYW5kc2NhcGUlMjBzdW5saWdodHxlbnwxfDB8fHwxNzgwODQ1NDQ5fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Seasons are caused by the 23.4-degree tilt of Earth's axis, not by the planet's distance from the Sun. The tilt changes how directly sunlight strikes each hemisphere over the course of the year.",
  "content": [
    {
      "type": "paragraph",
      "text": "Ask people why summer is hot and winter is cold and a surprising number will tell you it is because the Earth is closer to the Sun in summer. It is an intuitive answer, and it is wrong. Earth's distance from the Sun barely changes over the year, and we are actually slightly closer to the Sun in early January, the depth of winter in the Northern Hemisphere. The real cause of the seasons is something far more elegant: the planet is tilted."
    },
    {
      "type": "heading",
      "text": "It's the Tilt, Not the Distance"
    },
    {
      "type": "paragraph",
      "text": "Earth's axis, the imaginary line it spins around, is not straight up and down relative to its orbit. It leans over by about 23.4 degrees, and crucially, it keeps pointing in the same direction in space all year as the planet circles the Sun. That means for half the year the Northern Hemisphere is tipped toward the Sun, and for the other half it is tipped away. The hemisphere leaning toward the Sun gets summer; the one leaning away gets winter."
    },
    {
      "type": "heading",
      "text": "Why a Steeper Sun Means More Heat"
    },
    {
      "type": "paragraph",
      "text": "When your hemisphere is tilted toward the Sun, two things happen at once. The Sun climbs higher in the sky, so its rays strike the ground more directly instead of at a shallow, glancing angle, concentrating their energy on a smaller patch of land. And the days grow longer, giving that energy more hours to accumulate. Together, a higher Sun and longer days are what make summer warm. In winter the Sun stays low, the light spreads thin across the surface, and the short days never let the ground catch up. A simple way to picture it is a flashlight: shine it straight down onto a table and you get a small, bright circle; tilt it and the same beam smears across a much larger, dimmer oval. The Sun does exactly that to the ground as its angle rises and falls through the year."
    },
    {
      "type": "heading",
      "text": "Solstices and Equinoxes"
    },
    {
      "type": "paragraph",
      "text": "Four moments mark the rhythm of the year. The summer solstice is the day the Sun reaches its highest point and daylight is longest; the winter solstice is its mirror image, the shortest day. Halfway between them are the two equinoxes, when Earth's tilt leans neither toward nor away from the Sun and day and night are nearly equal everywhere on the planet. These are not arbitrary calendar dates, they are real astronomical events you can observe in the changing length of shadows through the year."
    },
    {
      "type": "heading",
      "text": "Why the Hemispheres Flip"
    },
    {
      "type": "paragraph",
      "text": "Because the tilt that gives one hemisphere its summer is the same tilt that turns the other away from the Sun, the seasons are always reversed across the equator. When it is July and beach weather in Italy, it is the heart of winter in Argentina. When Australians are decorating for a hot Christmas, Canadians are shovelling snow. This single fact trips up a lot of people, but it falls straight out of the geometry once you picture the tilted planet circling the Sun."
    },
    {
      "type": "heading",
      "text": "The Tropics, the Poles, and the Midnight Sun"
    },
    {
      "type": "paragraph",
      "text": "The tilt also draws some of the most important lines on any map. The Tropics of Cancer and Capricorn mark the farthest north and south the Sun can ever appear directly overhead, exactly 23.4 degrees, the angle of the tilt. The Arctic and Antarctic Circles mark where the tilt is extreme enough that the Sun can stay above the horizon for a full 24 hours in summer, the famous midnight sun, or fail to rise at all in the depths of winter, the polar night."
    },
    {
      "type": "heading",
      "text": "Watching the Seasons From Orbit"
    },
    {
      "type": "paragraph",
      "text": "From satellites, the seasons are written across the whole planet. Snow lines march toward the equator and retreat again. Forests shift colour. The vast green pulse of vegetation sweeps north across the continents each spring and fades in autumn. Even sea ice expands and contracts around the poles on a yearly cycle. The same axial tilt that decides whether you need a coat is visible from space as the entire surface of the Earth breathing in and out with the year."
    }
  ]
};
